import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { Scrollbars } from '@meitu/saas-ui';
import { NORMAL_LIST_STYLES, type IListStyleMaterial } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { type ListStyleStruct } from '@/types';
import { Controller } from './controller';
import { type BaseProps } from './types';
import './panel.less';

export const Panel: React.FC<BaseProps> = ({
    list,
    activeId,
    activeConfig,
    onCancelListStyle,
    onSetListStyle,
    onUpdateConfig,
}) => {
    const activeIdx = (() => {
        if (!activeId) return -1;
        return list.findIndex((item) => item.id === activeId);
    })();

    const splitIdx = (() => {
        if (activeIdx === -1 || !activeConfig) return -1;
        return Math.floor((activeIdx + 1) / 3) * 3 + 2;
    })();

    const leadingList = splitIdx === -1 ? list : list.slice(0, splitIdx);
    const trailingList = splitIdx === -1 ? [] : list.slice(splitIdx);

    const containerHeight = (() => {
        const row = Math.ceil((list.length + 1) / 3);
        const CARD_HEIGHT = 52 + 2 + 2; // 2 + 2: border-top + border-bottom
        const MARGIN_BOTTOM = 8;
        return row * CARD_HEIGHT + (row - 1) * MARGIN_BOTTOM;
    })();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrollbarsRef = useRef<any>(null);

    const handleCancelListStyle = () => {
        onCancelListStyle();
    };

    const handleSetListStyle = (item: ListStyleStruct) => {
        if (activeId === item.id) return;
        onSetListStyle(item);
    };

    const handleUpdateScale = (value: number, options = { writeHistory: false }) => {
        if (!activeConfig) return;

        const nextConfig: IListStyleMaterial = {
            ...activeConfig,
            scale: value,
        };

        if (onUpdateConfig) onUpdateConfig(nextConfig, options);
    };

    const handleUpdatePieceColor = (idx: number, value: string, options = { writeHistory: false }) => {
        if (!activeConfig) return;

        const nextColors = activeConfig.iconColors.map((item, i) => {
            if (i === idx) {
                return { ...item, color: value };
            }
            return item;
        });

        const nextConfig: IListStyleMaterial = {
            ...activeConfig,
            iconColors: nextColors,
        };

        if (onUpdateConfig) onUpdateConfig(nextConfig, options);
    };

    const handleUpdateListOrderColor = (value: string, options = { writeHistory: false }) => {
        if (!activeConfig) return;

        const nextConfig: IListStyleMaterial = {
            ...activeConfig,
            fontColor: value,
        };

        if (onUpdateConfig) onUpdateConfig(nextConfig, options);
    };

    useEffect(() => {
        if (activeIdx === -1) return;

        const activeEl = document.querySelector(`.list-style-panel__card--active`) as HTMLElement;
        if (activeEl) requestAnimationFrame(() => scrollbarsRef.current?.scrollTop(activeEl.offsetTop));
    }, [activeIdx]);

    const renderCardContent = (data: ListStyleStruct) => {
        const { thumbnail, style } = data;

        if (style === 'custom') {
            return <img src={thumbnail.url} />;
        }

        const shape = NORMAL_LIST_STYLES[style];
        const orders = shape.split(' ');

        return (
            <>
                {orders.map((order, idx) => {
                    return (
                        <div className="list-style-panel__seg" key={`${style}-${order}-${idx}`}>
                            <span className="list-style-panel__seg-order">{order}</span>
                            <span className="list-style-panel__seg-line"></span>
                        </div>
                    );
                })}
            </>
        );
    };

    return (
        <div className="list-style-panel" style={{ height: containerHeight }}>
            <Scrollbars autoHide scrollbarsRef={scrollbarsRef}>
                <div className="list-style-panel__scroll">
                    <div className="list-style-panel__list">
                        <div className="list-style-panel__card" onClick={handleCancelListStyle}>
                            <IconFont name="SlashCircleBold" fontSize={26} />
                        </div>
                        {leadingList.map((item) => {
                            return (
                                <div
                                    key={item.id}
                                    className={classNames('list-style-panel__card', {
                                        'list-style-panel__card--normal': item.style !== 'custom',
                                        'list-style-panel__card--active': activeId === item.id,
                                    })}
                                    onClick={() => handleSetListStyle(item)}
                                >
                                    {renderCardContent(item)}
                                </div>
                            );
                        })}
                    </div>

                    {activeConfig && activeIdx !== -1 ? (
                        <Controller
                            config={activeConfig}
                            onScaleChange={handleUpdateScale}
                            onPieceColorChange={handleUpdatePieceColor}
                            onListOrderColorChange={handleUpdateListOrderColor}
                        />
                    ) : null}

                    {trailingList.length > 0 ? (
                        <div className="list-style-panel__list">
                            {trailingList.map((item) => {
                                return (
                                    <div
                                        key={item.id}
                                        className={classNames('list-style-panel__card', {
                                            'list-style-panel__card--normal': item.style !== 'custom',
                                            'list-style-panel__card--active': activeId === item.id,
                                        })}
                                        onClick={() => handleSetListStyle(item)}
                                    >
                                        {renderCardContent(item)}
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            </Scrollbars>
        </div>
    );
};
